import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { fetchStaffConfig, exchangeAuthCode, validateStaffToken } from '../../modules/enrollment/api'
import { bindCacheToUser } from '../cache/localCache'
import {
  TOKEN_KEY,
  installFetchInterceptor,
  refreshSession,
  endSession,
  setSessionExpiredHandler,
  previewToken,
  setPreviewToken,
  setPreviewExpiredHandler,
} from './session'

// Google OAuth restricted to @littlescholarsnyc.com. ops-enrollment-api
// exchanges the Google code and issues the portal's own session:
//   - access token (1 h) -> localStorage, sent as Bearer to every backend
//   - refresh token      -> HttpOnly cookie on auth.littlescholarsnyc.com,
//                           sliding 14 days, hard limit 60 days
// See session.ts for the silent refresh / forced sign-out on 401, and
// ops-enrollment-api /staff/auth-code, /staff/refresh, /staff/logout.

installFetchInterceptor()

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.'

// staff.html hardcoded this to apply.littlescholarsnyc.com/staff. Ops-portal
// needs its own registered redirect URI — override via env once decided.
const AUTH_BASE: string = import.meta.env.VITE_ENROLLMENT_API_BASE ?? 'https://ops-enrollment-api-REPLACE-uc.a.run.app'

const REDIRECT_URI =
  import.meta.env.VITE_STAFF_REDIRECT_URI ?? `${window.location.origin}/enrollment/auth/callback`

// Job role from the canon (location_staff.role) — shown in the account menu. What a person may open is NOT decided by the role in the frontend: it comes from
// `modules` and `centers`, which /staff/auth resolves from Settings -> Access (doc 38 §Access).
export type StaffRole = string

export type AccessLevel = 'none' | 'view' | 'edit'

export type ModuleKey =
  | 'enrollment'
  | 'enrollment_all_documents'
  | 'enrollment_start_date'
  | 'enrollment_mark_started'
  | 'enrollment_mark_waiting'
  | 'enrollment_mark_cancelled'
  | 'enrollment_remove'
  | 'enrollment_deposit_date'
  | 'enrollment_doc_status'
  | 'students'
  | 'students_dashboard'
  | 'students_capacity'
  | 'students_exit_interviews'
  | 'students_voucher'
  | 'students_planned_departure'
  | 'staff'
  | 'staff_onboarding'
  | 'maintenance'
  | 'inventory'
  | 'billing_dashboard'
  | 'billing_debts'
  | 'billing_adjustments'
  | 'marketing'
  | 'sales'
  | 'recruitment'
  | 'recruitment_spend'
  | 'recruitment_settings'
  | 'loyalty_dashboard'
  | 'loyalty_requests'
  | 'loyalty_requests_cancel'
  | 'loyalty_support'
  | 'loyalty_exit_interviews'
  | 'loyalty_reviews'
  | 'loyalty_video_reviews'
  | 'loyalty_referrals'
  | 'loyalty_events'
  | 'loyalty_events_delete_own'
  | 'loyalty_events_delete_any'
  | 'loyalty_points'
  | 'loyalty_settings'
  | 'loyalty_families'
  | 'surveys'
  | 'assistant'
  | 'knowledge'
  | 'settings'
  | 'data_admin'

export interface StaffUser {
  email: string
  name: string
  role: StaffRole
  /** Name of the role as set in Settings → Access (roles added there have no name in code). */
  roleLabel?: string
  /** The centre when the person covers exactly one (kept for older screens). */
  location?: string
  /** Display names of the person's centres; null = every centre. */
  centers: string[] | null
  modules: Partial<Record<ModuleKey, AccessLevel>>
  /** Set while someone previews the portal as this person (Settings → Access → Preview as): who is looking. */
  previewBy?: string
}

interface AuthContextValue {
  user: StaffUser | null
  isLoading: boolean
  isSigningIn: boolean
  error: string | null
  /** Redirects to Google's consent screen. */
  startLogin: () => Promise<void>
  /** Call once, from the OAuth callback route, with the ?code= param. */
  completeLogin: (code: string) => Promise<void>
  logout: () => void
  /** Settings → Access → Preview as (2026-10-18): see the portal as this person, read-only, for 30 minutes. */
  startPreview: (email: string, name: string) => Promise<void>
  endPreview: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function toUser(data: {
  email: string
  name: string
  role: string
  location?: string | null
  centers?: string[] | null
  modules?: Partial<Record<ModuleKey, AccessLevel>>
  preview_by?: string | null
  role_label?: string | null
}): StaffUser {
  bindCacheToUser(data.email)
  return {
    email: data.email,
    name: data.name,
    role: data.role,
    roleLabel: data.role_label ?? undefined,
    location: data.location ?? undefined,
    // A response without `centers` predates the access model: treat as "no center" (fail closed).
    centers: data.centers === undefined ? [] : data.centers,
    modules: data.modules ?? {},
    previewBy: data.preview_by ?? undefined,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StaffUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Prefetched as soon as the app mounts (not on click) so a cold
  // ops-enrollment-api instance (Cloud Run scale-to-zero) warms up during
  // page load instead of during the click. A click that silently waits on
  // a cold start with no visual feedback looks like a dead button — this
  // is the same class of bug already fixed once on the vanilla
  // enrollment-portal/staff.html, ported here.
  const clientIdPromiseRef = useRef<Promise<string | null> | null>(null)
  function prefetchClientId(): Promise<string | null> {
    if (!clientIdPromiseRef.current) {
      clientIdPromiseRef.current = fetchStaffConfig()
        .then((config) => config.client_id)
        .catch(() => null)
    }
    return clientIdPromiseRef.current
  }

  // Any API call whose session cannot be refreshed lands here: sign out and
  // show the login screen instead of leaving "Invalid token" errors on screen.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      localStorage.removeItem(TOKEN_KEY)
      setUser(null)
      setError(SESSION_EXPIRED_MESSAGE)
    })
    return () => setSessionExpiredHandler(null)
  }, [])

  // Preview over (30 minutes, or the token was refused): back to the person's own view.
  useEffect(() => {
    setPreviewExpiredHandler(() => {
      setPreviewToken(null)
      window.location.href = '/settings?section=portal-access'
    })
    return () => setPreviewExpiredHandler(null)
  }, [])

  useEffect(() => {
    prefetchClientId()
    const restore = async () => {
      // On the OAuth callback page completeLogin() signs in. A parallel refresh
      // here would race it and could wipe the new session.
      if (window.location.pathname.startsWith('/enrollment/auth/callback')) return
      // Teachers' inventory upload page has no login (doc 70)
      if (window.location.pathname.startsWith('/inventory/upload/')) return
      // Preview as: show the portal as the previewed person (their token is kept for this tab only).
      const preview = previewToken()
      if (preview) {
        try {
          setUser(toUser(await validateStaffToken(preview)))
          return
        } catch {
          setPreviewToken(null)
        }
      }
      const stored = localStorage.getItem(TOKEN_KEY)
      if (stored) {
        try {
          setUser(toUser(await validateStaffToken(stored)))
          return
        } catch {
          localStorage.removeItem(TOKEN_KEY)
        }
      }
      // No valid access token: try the refresh cookie (e.g. the browser was
      // closed overnight). No cookie or a refused refresh -> login screen.
      try {
        const session = await refreshSession()
        if (session) setUser(toUser(session))
      } catch {
        /* auth service unreachable - show the login screen */
      }
    }
    restore().finally(() => setIsLoading(false))
  }, [])

  const startLogin = useCallback(async () => {
    setError(null)
    setIsSigningIn(true)
    const clientId = await prefetchClientId()
    if (!clientId) {
      setError('Failed to load sign-in configuration.')
      setIsSigningIn(false)
      return
    }
    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth')
    url.searchParams.set('client_id', clientId)
    url.searchParams.set('redirect_uri', REDIRECT_URI)
    url.searchParams.set('response_type', 'code')
    url.searchParams.set('scope', 'openid email profile')
    url.searchParams.set('hd', 'littlescholarsnyc.com')
    url.searchParams.set('access_type', 'online')
    url.searchParams.set('prompt', 'select_account')
    window.location.href = url.toString()
  }, [])

  const completeLogin = useCallback(async (code: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await exchangeAuthCode(code, REDIRECT_URI)
      localStorage.setItem(TOKEN_KEY, data.id_token) // carries the portal access token now
      setUser(toUser(data))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    setPreviewToken(null)
    void endSession()
    setUser(null)
  }, [])

  const startPreview = useCallback(async (email: string, name: string) => {
    const own = localStorage.getItem(TOKEN_KEY)
    const res = await fetch(`${AUTH_BASE}/staff/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${own ?? ''}` },
      body: JSON.stringify({ email, name }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.detail ?? `Could not start the preview (${res.status})`)
    setPreviewToken(data.access_token)
    window.location.href = '/' // a clean start: every screen loads as the previewed person
  }, [])

  const endPreview = useCallback(() => {
    setPreviewToken(null)
    window.location.href = '/settings?section=portal-access'
  }, [])

  return (
    <AuthContext.Provider value={{ user, isLoading, isSigningIn, error, startLogin, completeLogin, logout, startPreview, endPreview }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

export function getStaffToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

const RANK: Record<AccessLevel, number> = { none: 0, view: 1, edit: 2 }

/** May this person open `module` at `level` (default view)? Missing = none. */
export function can(user: StaffUser | null | undefined, module: ModuleKey, level: AccessLevel = 'view'): boolean {
  const have = user?.modules?.[module] ?? 'none'
  return RANK[have] >= RANK[level]
}

/** True when the person sees only their own centres (server filters too). */
export function isScoped(user: StaffUser | null | undefined): boolean {
  return !!user && user.centers !== null
}

/** The one centre of a single-centre person, else null. */
export function singleCenter(user: StaffUser | null | undefined): string | null {
  return user?.centers && user.centers.length === 1 ? user.centers[0] : null
}
